import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Divider, useTheme, Alert, AlertTitle, Collapse } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import SignatureCanvas from 'react-signature-canvas';
import { useTenantStore } from '../../stores/TenantStore';

/*
Time in milliseconds in which the alert is displayed after submitting the signature
*/
const _timeout_for_AlertMessage = 3000

/*
idea of different cursor for signature
*/
const cursor = ["quill", "ink", "ink-gold", "pen"]

interface IModalContentProps {
    handleCancel?: () => void;
}

const ModalContent: React.FC<IModalContentProps> = ({ handleCancel }) => {
    const SIG_CANVAS_WIDTH = 900
    const SIG_CANVAS_HEIGHT = 300
    const theme = useTheme();
    const [canvasWidth, setCanvasWidth] = useState(SIG_CANVAS_WIDTH);
    const [canvasHeight, setCanvasHeight] = useState(SIG_CANVAS_HEIGHT);

    const [showAlert, setShowAlert] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');

    const updateCanvasSize = () => {
        let newWidth;

        if (window.innerWidth < theme.breakpoints.values.sm) {
            newWidth = window.innerWidth;
        } else if (window.innerWidth >= theme.breakpoints.values.sm && window.innerWidth < theme.breakpoints.values.md) {
            newWidth = window.innerWidth * 0.9;
        } else {
            newWidth = SIG_CANVAS_WIDTH;
        }

        setCanvasWidth(newWidth);
        setCanvasHeight(newWidth / (SIG_CANVAS_WIDTH / SIG_CANVAS_HEIGHT));
    };

    const sigCanvas = useRef<SignatureCanvas>(null);
    const { dispatchTenant, sendRegistrationSheet, setIsRequestPending, updateActiveState } = useTenantStore()
    const penColor = theme.palette.mode === 'dark' ? 'white' : 'black';
    const clearCanvas = () => {
        if (sigCanvas.current) {
            sigCanvas.current.clear();
        }
    };

    useEffect(() => {
        window.addEventListener('resize', updateCanvasSize);
        updateCanvasSize();

        return () => {
            window.removeEventListener('resize', updateCanvasSize);
        };
    }, []);

    const saveSignature = async () => {
        if (sigCanvas.current) {
            //stop sending new requests so that the alert can be displayed
            setIsRequestPending(false)

            let signatureDataURL;
            if (theme.palette.mode === 'dark') {
                // Convert the signature to black only in dark mode
                signatureDataURL = await convertSignatureToBlack(sigCanvas, canvasWidth, canvasHeight);
            } else {
                // In light mode, use the original signature
                signatureDataURL = sigCanvas.current.toDataURL();
            }

            if (signatureDataURL) {
                const response = await sendRegistrationSheet(signatureDataURL);

                setShowAlert(true);

                if (response.success) {
                    setAlertSeverity('success');
                    setAlertTitle('Vielen Dank');
                    setAlertMessage('Ihr Meldezettel wurde erfolgreich unterschrieben');
                } else {
                    setAlertSeverity('error');
                    setAlertTitle('Error ' + response.errorCode);
                    setAlertMessage(response.errorMessage || "");
                }
                setTimeout(() => {
                    updateActiveState("emptyResponse")
                    setIsRequestPending(true)
                    dispatchTenant({ type: "CLEAR" });
                }, _timeout_for_AlertMessage);
            }
        } else {
            setAlertSeverity('error');
            setAlertTitle('Übertragungsfehler');
            setAlertMessage("Beim Übertragen Ihrer Unterschrift ist ein Problem aufgetreten. Bitte deaktivieren Sie den Darkmode und versuchen Sie es noch einmal.");
        }
    };


    useEffect(() => {
        if (sigCanvas && sigCanvas.current) {
            const signaturePad = sigCanvas.current.getSignaturePad();
            signaturePad.minWidth = 2;
            signaturePad.maxWidth = 5;
            signaturePad.penColor = penColor;
        }
    }, [sigCanvas, penColor]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ width: canvasWidth, height: canvasHeight, mb: 2 }}>
                <SignatureCanvas ref={sigCanvas} canvasProps={{ width: canvasWidth, height: canvasHeight, style: { cursor: `url(${process.env.PUBLIC_URL}/${cursor[1]}.cur), auto` } }} />
            </Box>
            <Divider />
            <Collapse in={showAlert}>
                <Alert severity={alertSeverity}>
                    <AlertTitle>{alertTitle}</AlertTitle>
                    {alertMessage}
                </Alert>
            </Collapse>
            <Box sx={{
                mt: 2, mb: 1, mr: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'flex-end',
                '& > button': {
                    mb: { xs: 1, sm: 0 },
                    mr: { xs: 0, sm: 1 },
                },
                '& > button:last-child': {
                    mb: 0,
                    mr: 0
                }
            }}>
                <Button disabled={showAlert} startIcon={<CancelIcon />} onClick={handleCancel} sx={{ mr: 1 }} color="inherit">
                    Abbrechen (cancel)
                </Button>
                <Button disabled={showAlert} startIcon={<DeleteIcon />} onClick={() => { clearCanvas(); }} sx={{ mr: 1 }} color="inherit">
                    Löschen (clear)
                </Button>
                <Button disabled={showAlert} startIcon={<SendIcon />} onClick={() => { saveSignature() }} color="primary">
                    Senden (submit)
                </Button>
            </Box>
        </Box>
    );
}


export default ModalContent;


const convertSignatureToBlack = async (
    sigCanvasRef: React.RefObject<SignatureCanvas>,
    canvasWidth: number,
    canvasHeight: number
): Promise<string | null> => {
    if (!sigCanvasRef.current) {
        return null;
    }

    // Get the original signature data URL
    const originalDataURL = sigCanvasRef.current.toDataURL();

    // Create a temporary canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvasWidth;
    tempCanvas.height = canvasHeight;
    const ctx = tempCanvas.getContext('2d');
    if (!ctx) {
        return null;
    }

    // Draw the original signature onto the temporary canvas
    const image = new Image();
    image.src = originalDataURL;

    return new Promise<string>((resolve, reject) => {
        image.onload = () => {
            ctx.drawImage(image, 0, 0);

            // Get image data
            let imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
            let data = imageData.data;

            // Change non-transparent pixels to black
            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] !== 0) { // If pixel is not transparent
                    data[i] = 0;     // Red
                    data[i + 1] = 0; // Green
                    data[i + 2] = 0; // Blue
                }
            }

            // Put the modified image data back onto the canvas
            ctx.putImageData(imageData, 0, 0);

            // Get the modified data URL
            const modifiedDataURL = tempCanvas.toDataURL();
            resolve(modifiedDataURL);
        };

        image.onerror = (error) => {
            reject(error);
        };
    });
};